import { useState, useMemo, useEffect } from 'react';

/**
 * Stable empty list for optional filter props.
 * Never use `= []` as a default for arrays passed into this hook — a fresh
 * array each render retriggers the sync effect and can infinite-loop.
 */
export const EMPTY_FILTER_ITEMS: string[] = [];

export interface UseFilterDropdownStateOptions {
  /** All items to show in the list */
  items: string[];
  /** Currently selected items (from parent) */
  selectedItems: string[];
  /** Called immediately when the user toggles or resets a selection */
  onSelectionChange: (items: string[]) => void;
  /** When provided, dropdown open state is controlled by the parent (e.g. shared menu) */
  opened?: boolean;
  setOpened?: (open: boolean) => void;
}

function sameSelection(prev: Set<string>, next: string[]): boolean {
  return prev.size === next.length && next.every((item) => prev.has(item));
}

/**
 * Shared state for a filter dropdown: search + multi-select applied on each click.
 */
export function useFilterDropdownState({
  items,
  selectedItems,
  onSelectionChange,
  opened: controlledOpened,
  setOpened: controlledSetOpened,
}: UseFilterDropdownStateOptions) {
  const [internalOpened, setInternalOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selection, setSelection] = useState<Set<string>>(() => new Set(selectedItems));

  const isControlled = controlledOpened !== undefined && controlledSetOpened !== undefined;
  const opened = isControlled ? controlledOpened : internalOpened;
  const setOpened = isControlled ? controlledSetOpened! : setInternalOpened;

  // Bail out when contents match so an unstable selectedItems reference cannot loop.
  useEffect(() => {
    setSelection((prev) => (sameSelection(prev, selectedItems) ? prev : new Set(selectedItems)));
  }, [selectedItems]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase().trim();
    return items.filter((item) => item.toLowerCase().includes(q));
  }, [items, searchQuery]);

  const handleToggle = (item: string) => {
    const next = new Set(selection);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    setSelection(next);
    onSelectionChange(Array.from(next));
  };

  const handleReset = () => {
    setSelection(new Set());
    setSearchQuery('');
    onSelectionChange([]);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSelection(new Set(selectedItems));
      setSearchQuery('');
    }
    setOpened(open);
  };

  return {
    opened,
    handleOpenChange,
    searchQuery,
    setSearchQuery,
    filteredItems,
    /** @deprecated use `selection` — kept for existing call sites */
    pendingSelection: selection,
    selection,
    handleToggle,
    handleReset,
  };
}
